import { Link, useRouterState } from '@tanstack/react-router';
import styled from 'styled-components';
import { getBranding } from '@/config/branding';
import { useAuth } from '@/providers/AuthProvider';

const branding = getBranding();

interface NavItem {
  label: string;
  to: string;
  icon: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Popups', to: '/', icon: '▦' },
  { label: 'Usage & Billing', to: '/usage', icon: '◈' },
];

const SidebarWrapper = styled.aside`
  width: 240px;
  min-width: 240px;
  height: 100vh;
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e5e7eb;
  overflow: hidden;
`;

const BrandingArea = styled.div`
  padding: 20px 16px 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const AppName = styled(Link)`
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  text-decoration: none;
  letter-spacing: -0.01em;

  &:hover {
    color: #374151;
  }
`;

const Nav = styled.nav`
  flex: 1;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NavLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9ca3af;
  padding: 8px 8px 4px;
  display: block;
`;

interface NavLinkProps {
  $active: boolean;
}

const NavLink = styled(Link)<NavLinkProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: background 120ms, color 120ms;

  background: ${({ $active }) => ($active ? '#111827' : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : '#374151')};

  &:hover {
    background: ${({ $active }) => ($active ? '#1f2937' : '#f3f4f6')};
    color: ${({ $active }) => ($active ? '#fff' : '#111827')};
  }
`;

const NavIcon = styled.span`
  font-size: 14px;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
`;

const Footer = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export function Sidebar() {
  const { user } = useAuth();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  function isActive(to: string): boolean {
    if (to === '/') return currentPath === '/';
    return currentPath.startsWith(to);
  }

  return (
    <SidebarWrapper>
      <BrandingArea>
        <AppName to="/">{branding.appName}</AppName>
      </BrandingArea>

      <Nav>
        <NavLabel>Navigation</NavLabel>
        {NAV_ITEMS.map((item) => (
          <NavLink key={item.to} to={item.to} $active={isActive(item.to)}>
            <NavIcon>{item.icon}</NavIcon>
            {item.label}
          </NavLink>
        ))}
      </Nav>

      {user && (
        <Footer title={user.email}>{user.email}</Footer>
      )}
    </SidebarWrapper>
  );
}
